import { FC, useContext, useEffect, useState } from 'react';

import dayjs from 'dayjs';

import { LocalizationContext } from 'localization';
import type { TPayment, EPaymentItemType, IReportPayment } from 'types';

import { Chart } from './Chart';
import { IPaymentsChartProps } from './interfaces';
import s from './PaymentsChart.module.scss';

const getReportPayments = (
  payments: Array<TPayment>,
  startDate: string,
  endDate?: string
): Array<IReportPayment> => {
  let total = 0;

  const mappedPayments: Array<IReportPayment> = payments
    .filter((payment) => {
      if (payment.is_refunded) {
        return false;
      }

      if (payment.method === 'CARD' || payment.method === 'CASH') {
        return true;
      }
    })
    .sort(
      (p1, p2) =>
        dayjs(p1.created_at).valueOf() - dayjs(p2.created_at).valueOf()
    )
    .map((payment) => {
      total = Number((total + payment.sum).toFixed(2));

      return {
        ...payment,
        total,
        created_at: dayjs(payment.created_at).valueOf(),
        sumByItemType: payment.items.reduce<
          Partial<Record<EPaymentItemType, number>>
        >(
          (acc, item) => ({
            ...acc,
            [item.type]: (acc[item.type] ?? 0) + item.sum,
          }),
          {}
        ),
      };
    });

  return [
    { total: 0, sum: 0, created_at: dayjs(startDate).valueOf() },
    ...mappedPayments,
    { total, sum: 0, created_at: dayjs(endDate).valueOf() },
  ];
};

export const PaymentsChart: FC<IPaymentsChartProps> = ({ workShift }) => {
  const { t } = useContext(LocalizationContext);
  const [payments, setPayments] = useState<Array<IReportPayment>>([]);

  useEffect(() => {
    if (workShift.payments.length) {
      setPayments(
        getReportPayments(
          workShift.payments,
          workShift.created_at,
          workShift.finished_at
        )
      );
    }
  }, [workShift.payments]);

  return (
    <div className={s.container}>
      <header className={s.title}>{t('workShift.detail.chart')}</header>
      {payments.length ? (
        <Chart data={payments} />
      ) : (
        <span className={s['placeholder']}>
          {t('workShift.detail.chart.placeholder')}
        </span>
      )}
    </div>
  );
};
